import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiPost } from '../../services_1/apiClient';
import { useAuth } from '../../context/AuthContext';
import useSharedForecastFilters from '../../hooks/useSharedForecastFilters';
import { applyRoleLocks, getRoleLocks } from '../../utils_1/roleLocks';
import { buildAnomalyFallback } from '../../utils_1/forecastUtils';
import './ForecAICopilot.css';

const STORAGE_KEY = 'forec_ai_copilot_position';

const ROLE_LEVELS = {
  admin: ['national_day', 'brand_day', 'brand_location_day', 'brand_channel_day', 'sku_day', 'sku_location_day', 'sku_channel_day'],
  demand_planner: ['national_day', 'brand_day', 'brand_location_day', 'brand_channel_day', 'sku_day', 'sku_location_day', 'sku_channel_day'],
  brand_manager: ['national_day', 'brand_day', 'sku_day'],
  sales_manager: ['national_day', 'brand_channel_day'],
  sc_analyst: ['brand_day', 'sku_day', 'sku_location_day', 'sku_channel_day'],
  executive: ['national_day', 'brand_day', 'brand_location_day', 'brand_channel_day', 'sku_day'],
};

const loadInitialPosition = () => {
  if (typeof window === 'undefined') return { x: 24, y: 160 };
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)) {
      return {
        x: Math.min(Math.max(saved.x, 12), window.innerWidth - 96),
        y: Math.min(Math.max(saved.y, 88), window.innerHeight - 96),
      };
    }
  } catch (err) {
    // Ignore malformed saved positions.
  }
  return { x: Math.max(window.innerWidth - 430, 24), y: Math.max(window.innerHeight - 260, 120) };
};

const compactArray = (arr, limit = 8) => (Array.isArray(arr) ? arr.slice(0, limit) : undefined);

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const sumValues = (values) => (Array.isArray(values) ? values.reduce((sum, value) => sum + toNumber(value), 0) : 0);

const sumOptionalValues = (values) => (Array.isArray(values) && values.length
  ? values.reduce((sum, value) => sum + toNumber(value), 0)
  : null);

const isMinFilterQuestion = (message = '') => {
  const text = String(message || '').toLowerCase();
  return [
    'min quantity',
    'minimum quantity',
    'min qty',
    'minimum qty',
    'min quant',
    'minimum quant',
    'min percentile',
    'minimum percentile',
    'min percentle',
    'minimum percentle',
    'percentile filter',
    'min pct',
    'minpct',
  ].some(term => text.includes(term));
};

const buildMinFilterAnswer = (customerOrdersState) => {
  const filters = customerOrdersState?.filters || {};
  const current = [];
  if (filters.minQty !== undefined && filters.minQty !== null && filters.minQty !== '') {
    current.push(`Min Qty = ${filters.minQty}`);
  }
  if (filters.minPct !== undefined && filters.minPct !== null && filters.minPct !== '') {
    current.push(`Min Percentile = ${filters.minPct}`);
  }
  return [
    '**Customer Orders filters:**',
    '- **Min Qty:** keeps only order/forecast points whose quantity is at least this value. It removes very small orders from the chart and KPI calculations.',
    '- **Min Percentile:** keeps only points above the selected percentile cutoff. For example, 80 means focus on the higher-demand points, roughly the top 20% of the filtered range.',
    '- **Together:** Min Qty is an absolute cutoff; Min Percentile is a relative cutoff based on the selected data.',
    current.length ? `- **Current setting:** ${current.join(', ')}.` : '',
  ].filter(Boolean).join('\n');
};

const formatAggregationLevel = (level = '') =>
  String(level)
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('_');

const compactDrillResults = (results, metadata, limit = 8) => {
  if (!Array.isArray(results)) return undefined;
  const filters = metadata?.filters_applied || {};
  return results.slice(0, limit).map((result) => {
    const summary = result?.summary_metrics || {};
    const demandMetrics = summary?.demand_metrics || {};
    const subtitleParts = [
      result?.drill_context?.drill_value,
      filters.brand,
      filters.location,
      filters.channel,
    ].filter(Boolean);
    return {
      title: formatAggregationLevel(result?.level || result?.target_level || ''),
      subtitle: subtitleParts.slice(0, 3).join(' · '),
      level: result?.level,
      aggregation_key: result?.aggregation_key,
      records: summary?.total_records,
      total_demand: demandMetrics?.total_demand,
      average_daily_demand: demandMetrics?.average_daily_demand,
      drill_value: result?.drill_context?.drill_value,
    };
  });
};

const buildZeroPromotionalDates = (data) => {
  if (!data || typeof data !== 'object') return [];
  if (Array.isArray(data.forecasts) && data.forecasts.length > 0) {
    return buildZeroPromotionalDates(data.forecasts[0]);
  }
  const forecastData = data.forecast_data || {};
  const dates = getSeries(data, ['dates', 'labels']).length
    ? getSeries(data, ['dates', 'labels'])
    : getSeries(forecastData, ['dates', 'labels']);
  const promoValues = getSeries(data, ['promotional_forecast', 'promotional_demand_forecast', 'promotional_values', 'promo_values']).length
    ? getSeries(data, ['promotional_forecast', 'promotional_demand_forecast', 'promotional_values', 'promo_values'])
    : getSeries(forecastData, ['promotional_forecast', 'promotional_demand_forecast', 'promotional_values', 'promo_values']);
  if (!dates.length || !promoValues.length) return [];
  return dates
    .map((date, index) => ({ date, promotional_demand: toNumber(promoValues[index]) }))
    .filter((point) => point.date && point.promotional_demand === 0)
    .map((point) => point.date);
};

const clampPanelWidth = (width) => {
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const minWidth = viewportWidth <= 1100 ? Math.min(430, viewportWidth - 20) : 390;
  const maxWidth = viewportWidth <= 1100 ? Math.min(430, viewportWidth - 20) : Math.min(680, Math.max(420, viewportWidth * 0.55));
  return Math.round(Math.min(Math.max(Number(width) || viewportWidth * 0.35, minWidth), maxWidth));
};

const getSeries = (data, keys) => {
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }
  return [];
};

const PAGE_INTROS = {
  dashboard: 'Dashboard context: explain daily forecast, KPI cards, monthly summary, base/promo/total demand, confidence band, and anomalies.',
  simulation: 'Simulation context: explain the scenario forecast output, how the simulated change affects demand, confidence band, and scenario assumptions.',
  promotional: 'Promotional analysis context: explain base demand vs promotional uplift, promo intensity, promo/base ratio, and historical promo impact.',
  aggregation: 'Aggregation context: explain roll-up/drill-down level, records, total demand, quality score, and role-allowed aggregation levels.',
  adhoc: 'Ad-hoc context: explain previous value, new value, change amount, approval status, recent change history, and whether the change should overwrite dataset values.',
  customer_orders: 'Customer orders context: explain customer/SKU/location/channel order trend, total units, median/p90/p95, and customer mix.',
};

const buildGraphSummary = (data) => {
  if (!data || typeof data !== 'object') return null;
  const dates = getSeries(data, ['dates', 'labels']);
  const baseValues = getSeries(data, ['base_forecast', 'base_demand_forecast', 'base_values', 'base_demand']);
  const promoValues = getSeries(data, ['promotional_forecast', 'promotional_demand_forecast', 'promotional_values', 'promo_values']);
  const totalValues = getSeries(data, ['total_forecast', 'total_demand_forecast', 'total_values', 'forecast_values', 'values']);
  const upperValues = getSeries(data, ['confidence_upper']);
  const lowerValues = getSeries(data, ['confidence_lower']);
  const anomalyFlags = getSeries(data, ['anomaly_flags']);
  const anomalyNotes = getSeries(data, ['anomaly_notes']);
  const anomalyStrengths = getSeries(data, ['anomaly_strengths']);
  const anomalyDirections = getSeries(data, ['anomaly_directions']);
  const anomalyReasons = getSeries(data, ['anomaly_reasons']);
  if (!dates.length && !baseValues.length && !promoValues.length && !totalValues.length) return null;
  const resolvedTotal = totalValues.length
    ? totalValues.map(toNumber)
    : baseValues.map((value, index) => toNumber(value) + toNumber(promoValues[index]));
  const points = resolvedTotal.map((value, index) => ({
    date: dates[index],
    total: value,
    base: baseValues.length ? toNumber(baseValues[index]) : null,
    promo: promoValues.length ? toNumber(promoValues[index]) : null,
    anomaly_flag: Boolean(anomalyFlags[index]),
    anomaly_note: String(anomalyNotes[index] ?? '').trim(),
    anomaly_strength: toNumber(anomalyStrengths[index]),
    anomaly_direction: String(anomalyDirections[index] ?? '').trim(),
    anomaly_reason: String(anomalyReasons[index] ?? '').trim(),
  })).filter(point => point.date || point.total || point.base || point.promo);
  const sortedByTotal = [...points].sort((a, b) => b.total - a.total);
  const promoDays = promoValues.filter(value => toNumber(value) > 0).length;
  const rawAnomalyDetails = points
    .filter(point => point.anomaly_flag || point.anomaly_note || point.anomaly_reason)
    .slice(0, 20);
  let anomalyDetails = rawAnomalyDetails
    .filter((point, index, arr) => arr.findIndex(item => item.date === point.date) === index)
    .sort((a, b) => (b.anomaly_strength || 0) - (a.anomaly_strength || 0))
    .slice(0, 20);

  if (!anomalyDetails.length) {
    const fallbackAnomalies = buildAnomalyFallback(
      resolvedTotal,
      baseValues,
      promoValues,
      data.historical_summary || {}
    );
    anomalyDetails = fallbackAnomalies.flags
      .map((flag, index) => {
        if (!flag) return null;
        return {
          date: dates[index] || `Point ${index + 1}`,
          total: resolvedTotal[index] ?? 0,
          base: baseValues[index] ?? 0,
          promo: promoValues[index] ?? 0,
          anomaly_flag: true,
          anomaly_note: fallbackAnomalies.notes[index] || '',
          anomaly_strength: fallbackAnomalies.strengths[index] || 0,
          anomaly_direction: fallbackAnomalies.directions[index] || '',
          anomaly_reason: fallbackAnomalies.reasons[index] || '',
        };
      })
      .filter(Boolean)
      .slice(0, 20);
  }

  return {
    period_count: dates.length || points.length,
    first_period: dates[0],
    last_period: dates[dates.length - 1],
    total_visible_demand: sumValues(resolvedTotal),
    total_base_demand: sumOptionalValues(baseValues),
    total_promotional_demand: sumOptionalValues(promoValues),
    promo_days_or_periods: promoValues.length ? promoDays : null,
    promo_to_base_ratio: sumOptionalValues(baseValues) > 0 ? Number((sumOptionalValues(promoValues) / sumOptionalValues(baseValues)).toFixed(3)) : null,
    average_visible_demand: resolvedTotal.length ? sumValues(resolvedTotal) / resolvedTotal.length : null,
    highest_points: sortedByTotal.slice(0, 3),
    lowest_points: [...points].sort((a, b) => a.total - b.total).slice(0, 3),
    anomaly_details: anomalyDetails,
    confidence_band_sample: dates.slice(0, 5).map((date, index) => ({
      date,
      lower: toNumber(lowerValues[index]),
      upper: toNumber(upperValues[index]),
    })),
    monthly_summary: data.monthly_summary || null,
    metadata: data.metadata || null,
    scenario: data.scenario || data.scenario_details || data.metadata?.scenario || null,
    historical_summary: data.historical_summary || null,
    promotional_impact: data.promotional_impact || data.promotional_impact_analysis || null,
    data_quality: data.data_quality || null,
  };
};

const compactForecastData = (data) => {
  if (!data || typeof data !== 'object') return null;
  const forecastData = data.forecast_data || {};
  const metadata = data.metadata;
  return {
    level: data.level,
    target_level: data.target_level,
    current_level: data.current_level,
    metadata,
    summary_metrics: data.summary_metrics,
    monthly_summary: data.monthly_summary,
    regional_config: data.regional_config,
    parameters_used: data.parameters_used,
    data_quality: data.data_quality,
    graph_summary: buildGraphSummary(data) || buildGraphSummary(forecastData),
    zero_promotional_dates: buildZeroPromotionalDates(data),
    labels: compactArray(data.labels || data.dates || forecastData.dates, 12),
    dates: compactArray(data.dates || data.labels || forecastData.dates, 12),
    forecast_values: compactArray(data.forecast_values || forecastData.forecast_values || data.total_forecast || data.values, 12),
    base_values: compactArray(data.base_values || data.base_demand || data.base_forecast || data.base_demand_forecast || forecastData.base_values, 12),
    promotional_values: compactArray(data.promotional_values || data.promo_values || data.promotional_forecast || data.promotional_demand_forecast || forecastData.promotional_values, 12),
    total_values: compactArray(data.total_values || data.total_demand || data.total_forecast || data.values || forecastData.total_values, 12),
    confidence_upper: compactArray(data.confidence_upper, 12),
    confidence_lower: compactArray(data.confidence_lower, 12),
    anomaly_flags: compactArray(data.anomaly_flags || forecastData.anomaly_flags, 20),
    anomaly_notes: compactArray(data.anomaly_notes || forecastData.anomaly_notes, 20),
    anomaly_strengths: compactArray(data.anomaly_strengths || forecastData.anomaly_strengths, 20),
    anomaly_directions: compactArray(data.anomaly_directions || forecastData.anomaly_directions, 20),
    anomaly_reasons: compactArray(data.anomaly_reasons || forecastData.anomaly_reasons, 20),
    anomalies: compactArray(data.anomalies || forecastData.anomalies, 8),
    drill_results: compactDrillResults(data.drill_results, metadata, 8),
  };
};

const withGraphSummaryOverride = (forecastContext, chartContext) => {
  if (!forecastContext || !chartContext?.graph_summary) return forecastContext;
  return {
    ...forecastContext,
    graph_summary: chartContext.graph_summary,
    chart_context: chartContext,
  };
};

const buildVisibleContext = ({
  activeWindow,
  dataSummary,
  dailyForecastData,
  dashboardKpiContext,
  dashboardChartContext,
  simpleForecastData,
  simulationChartContext,
  simulationScenarioContext,
  promotionalData,
  aggregationState,
  customerOrdersState,
  adHocData,
}) => ({
  active_window: activeWindow,
  page_instruction: PAGE_INTROS[activeWindow] || 'General ForeC.AI page context.',
  data_summary: dataSummary ? {
    records: dataSummary.total_records || dataSummary.records || dataSummary.rows,
    date_range: dataSummary.date_range,
    active_dataset: dataSummary.active_dataset,
  } : null,
  dashboard_kpis: activeWindow === 'dashboard' ? dashboardKpiContext : null,
  daily_forecast: activeWindow === 'dashboard'
    ? withGraphSummaryOverride(compactForecastData(dailyForecastData), dashboardChartContext)
    : null,
  dashboard_forecast_item: activeWindow === 'dashboard'
    ? withGraphSummaryOverride(compactForecastData(dailyForecastData?.forecasts?.[0]), dashboardChartContext)
    : null,
  promotional_analysis: activeWindow === 'promotional' ? compactForecastData(promotionalData) : null,
  simulation: activeWindow === 'simulation'
    ? {
      ...withGraphSummaryOverride(compactForecastData(simpleForecastData), simulationChartContext),
      selected_context: simulationScenarioContext,
    }
    : null,
  aggregation: activeWindow === 'aggregation' ? {
    loading: aggregationState?.loading,
    selected_sku_id: aggregationState?.selectedSkuId,
    aggregation_data: compactForecastData(aggregationState?.aggregationData),
    drill_data: compactForecastData(aggregationState?.drillData),
    error: aggregationState?.error,
  } : null,
  customer_orders: activeWindow === 'customer_orders' ? {
    loading: customerOrdersState?.loading,
    error: customerOrdersState?.error,
    filters: customerOrdersState?.filters,
    stats: customerOrdersState?.stats,
    total_units: customerOrdersState?.totalUnits,
    average_per_day: customerOrdersState?.avgPerDay,
    horizon_days: customerOrdersState?.horizonDays,
    promo_days: customerOrdersState?.promoDays,
    rows_count: customerOrdersState?.rowsCount,
    areaTrend: customerOrdersState?.areaTrend,
    alerts: compactArray(customerOrdersState?.alerts, 12),
    alertCount: customerOrdersState?.alertCount || 0,
    customer_mix: compactArray(customerOrdersState?.customerMix, 8),
    sku_mix: compactArray(customerOrdersState?.skuMix, 8),
    location_mix: compactArray(customerOrdersState?.locationMix, 8),
    sample_rows: compactArray(customerOrdersState?.sampleRows, 8),
  } : null,
  ad_hoc: activeWindow === 'adhoc' ? adHocData : null,
});

const roleLabel = (role = '') => String(role || 'demand_planner')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

const renderInlineMarkdown = (text, keyPrefix) => {
  const parts = String(text || '').split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    const bold = part.match(/^\*\*([^*]+)\*\*$/);
    if (bold) {
      return <strong key={`${keyPrefix}-b-${index}`}>{bold[1]}</strong>;
    }
    return <React.Fragment key={`${keyPrefix}-t-${index}`}>{part}</React.Fragment>;
  });
};

const renderMessageContent = (content) => {
  const lines = String(content || '')
    .split(/\n+/)
    .map(line => line.trim())
    .filter(Boolean);
  if (!lines.length) return null;

  const nodes = [];
  let bulletItems = [];
  const flushBullets = () => {
    if (!bulletItems.length) return;
    const listIndex = nodes.length;
    nodes.push(
      <ul key={`ul-${listIndex}`}>
        {bulletItems.map((item, itemIndex) => (
          <li key={`li-${listIndex}-${itemIndex}`}>
            {renderInlineMarkdown(item, `li-${listIndex}-${itemIndex}`)}
          </li>
        ))}
      </ul>
    );
    bulletItems = [];
  };

  lines.forEach((line, index) => {
    const bullet = line.match(/^[-*]\s+(.*)$/);
    if (bullet) {
      bulletItems.push(bullet[1]);
      return;
    }
    flushBullets();
    nodes.push(
      <p key={`p-${index}`}>
        {renderInlineMarkdown(line, `p-${index}`)}
      </p>
    );
  });
  flushBullets();
  return nodes;
};

const ForecAICopilot = ({
  activeWindow,
  onOpenChange,
  panelWidth,
  onPanelWidthChange,
  dataSummary,
  dashboardKpiContext,
  dashboardChartContext,
  dailyForecastData,
  simpleForecastData,
  simulationChartContext,
  simulationScenarioContext,
  promotionalData,
  aggregationState,
  customerOrdersState,
  adHocData,
}) => {
  const { user } = useAuth();
  const [sharedFilters] = useSharedForecastFilters();
  const role = user?.role || 'demand_planner';
  const roleLocks = getRoleLocks(role);
  const scopedFilters = useMemo(
    () => applyRoleLocks(sharedFilters || {}, role),
    [sharedFilters, role]
  );

  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(loadInitialPosition);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Ask me about this forecast, anomaly, KPI, customer order, ad-hoc change, or aggregation result. I will stay inside your role scope.',
    },
  ]);
  const dragRef = useRef(null);
  const resizeRef = useRef(null);
  const inputRef = useRef(null);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    onOpenChange?.(open);
    return () => onOpenChange?.(false);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    onPanelWidthChange?.(clampPanelWidth(panelWidth));
  }, [open, onPanelWidthChange, panelWidth]);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 120);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(position));
  }, [position]);

  const startDrag = useCallback((event) => {
    isDraggingRef.current = false;
    const point = event.touches?.[0] || event;
    dragRef.current = {
      startX: point.clientX,
      startY: point.clientY,
      baseX: position.x,
      baseY: position.y,
    };
    event.preventDefault();
  }, [position]);

  const startResize = useCallback((event) => {
    const point = event.touches?.[0] || event;
    resizeRef.current = {
      startX: point.clientX,
      startWidth: clampPanelWidth(panelWidth),
    };
    event.preventDefault();
    event.stopPropagation();
  }, [panelWidth]);

  useEffect(() => {
    const handleMove = (event) => {
      if (!dragRef.current) return;
      const point = event.touches?.[0] || event;
      const deltaX = point.clientX - dragRef.current.startX;
      const deltaY = point.clientY - dragRef.current.startY;
      if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
        isDraggingRef.current = true;
      }
      const width = open ? 392 : 92;
      const height = open ? 520 : 92;
      const nextX = dragRef.current.baseX + (point.clientX - dragRef.current.startX);
      const nextY = dragRef.current.baseY + (point.clientY - dragRef.current.startY);
      setPosition({
        x: Math.min(Math.max(nextX, 10), window.innerWidth - width - 10),
        y: Math.min(Math.max(nextY, 78), window.innerHeight - height - 10),
      });
    };
    const handleResizeMove = (event) => {
      if (!resizeRef.current) return;
      const point = event.touches?.[0] || event;
      const deltaX = point.clientX - resizeRef.current.startX;
      onPanelWidthChange?.(clampPanelWidth(resizeRef.current.startWidth - deltaX));
      event.preventDefault?.();
    };
    const stopDrag = () => {
      dragRef.current = null;
      resizeRef.current = null;
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mousemove', handleResizeMove);
    window.addEventListener('mouseup', stopDrag);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchmove', handleResizeMove, { passive: false });
    window.addEventListener('touchend', stopDrag);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mousemove', handleResizeMove);
      window.removeEventListener('mouseup', stopDrag);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchmove', handleResizeMove);
      window.removeEventListener('touchend', stopDrag);
    };
  }, [open, onPanelWidthChange]);

  const sendMessage = async (event) => {
    event?.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || loading) return;

    const userMessage = { role: 'user', content: trimmed };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setInput('');

    if (isMinFilterQuestion(trimmed)) {
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: buildMinFilterAnswer(customerOrdersState),
          provider: 'local_filter_help',
        },
      ]);
      return;
    }

    setLoading(true);

    try {
      const response = await apiPost('/api/llm/chat', {
        message: trimmed,
        conversation: nextMessages.slice(-6),
        role_context: {
          role,
          username: user?.username || user?.name || '',
          full_name: user?.full_name || user?.name || '',
          lockedBrand: roleLocks.lockedBrand,
          lockedChannel: roleLocks.lockedChannel,
        },
        page_context: {
          active_window: activeWindow,
          page_instruction: PAGE_INTROS[activeWindow] || 'General ForeC.AI page context.',
          filters: scopedFilters,
          allowed_aggregation_levels: ROLE_LEVELS[role] || ROLE_LEVELS.demand_planner,
          visible_context: buildVisibleContext({
            activeWindow,
            dataSummary,
            dashboardKpiContext,
            dashboardChartContext,
            dailyForecastData,
            simpleForecastData,
            simulationChartContext,
            simulationScenarioContext,
            promotionalData,
            aggregationState,
            customerOrdersState,
            adHocData,
          }),
        },
      });
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: response?.answer || 'I could not prepare an answer from the available context.',
          provider: response?.provider,
        },
      ]);
    } catch (err) {
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: `I could not reach the Forecast IQ service: ${err.message}`,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const scopeText = roleLocks.lockedBrand
    ? `Scope: ${roleLocks.lockedBrand}`
    : roleLocks.lockedChannel
      ? `Scope: ${roleLocks.lockedChannel}`
      : `Scope: ${roleLabel(role)}`;

  const quickQuestions = [
    'Explain this page',
    'Why is demand changing?',
    activeWindow === 'aggregation' ? 'What aggregation levels can I use?' : 'Check selected filters',
  ];

  return (
    <div
      className={`forec-copilot ${open ? 'is-open' : 'is-docked'}`}
      style={open ? { width: `${clampPanelWidth(panelWidth)}px` } : { left: position.x, top: position.y }}
    >
      {!open && (
        <button
          type="button"
          className="forec-copilot-orb"
          onMouseDown={startDrag}
          onTouchStart={startDrag}
          onClick={(e) => {
            if (isDraggingRef.current) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            setOpen(true);
          }}
          aria-label="Open Forecast IQ"
        >
          <span className="forec-orb-grid" />
          <span className="forec-orb-core">
            <svg className="forec-sales-icon" viewBox="0 0 96 64" aria-hidden="true">
              <defs>
                <linearGradient id="forecSalesBarGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ffffff" />
                  <stop offset="100%" stopColor="#62c4ff" />
                </linearGradient>
              </defs>
              <rect x="14" y="35" width="10" height="17" rx="5" />
              <rect x="30" y="24" width="10" height="28" rx="5" />
              <rect x="46" y="31" width="10" height="21" rx="5" />
              <rect x="62" y="15" width="10" height="37" rx="5" />
              <path d="M13 33 C24 20, 32 18, 41 29 S59 39, 72 14" />
              <circle cx="13" cy="33" r="4" />
              <circle cx="41" cy="29" r="4" />
              <circle cx="72" cy="14" r="4" />
            </svg>
          </span>
        </button>
      )}

      {open && (
        <section className="forec-copilot-panel" aria-label="Forecast IQ">
          <button
            type="button"
            className="forec-copilot-resize"
            onMouseDown={startResize}
            onTouchStart={startResize}
            aria-label="Resize Forecast IQ panel"
            title="Drag to resize"
          />
          <div className="forec-copilot-header">
            <div className="forec-copilot-hero" aria-hidden="true">
              <div className="forec-holo-orb">
                <span className="forec-holo-grid forec-holo-grid-lat" />
                <span className="forec-holo-grid forec-holo-grid-lon" />
                <span className="forec-holo-wave" />
                <span className="forec-holo-ring forec-holo-ring-one"><span /></span>
                <span className="forec-holo-ring forec-holo-ring-two"><span /></span>
                <span className="forec-holo-ring forec-holo-ring-three"><span /></span>
              </div>
              <span className="forec-holo-shadow" />
            </div>
            <div className="forec-copilot-heading">
              <div className="forec-copilot-title">Forecast IQ</div>
              <div className="forec-copilot-subtitle">Every chart explained in your role scope</div>
              <div className="forec-copilot-context">{scopeText}</div>
            </div>
            <button
              type="button"
              className="forec-copilot-close"
              onClick={() => setOpen(false)}
              aria-label="Close copilot"
            >
              x
            </button>
          </div>

          <div className="forec-copilot-scope">
            <strong>{roleLabel(role)}</strong>
            <span>{activeWindow.replace(/_/g, ' ')} context is active.</span>
          </div>

          <div className="forec-copilot-messages">
            {messages.map((message, index) => (
              <div key={`${message.role}-${index}`} className={`forec-message forec-message-${message.role}`}>
                <div className="forec-message-bubble">
                  <div className="forec-message-text">
                    {renderMessageContent(message.content)}
                  </div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="forec-message forec-message-assistant">
                <div className="forec-message-bubble forec-thinking">
                  <span /> <span /> <span />
                </div>
              </div>
            )}
          </div>

          <div className="forec-quick-questions">
            {quickQuestions.map((question, index) => (
              <button
                key={question}
                type="button"
                onClick={() => setInput(question)}
                disabled={loading}
              >
                <span className="forec-quick-icon">{index + 1}</span>
                <span>{question}</span>
              </button>
            ))}
          </div>

          <form className="forec-copilot-input" onSubmit={sendMessage}>
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about this chart, KPI, anomaly, or dataset row..."
              maxLength={700}
            />
            <button type="submit" disabled={!input.trim() || loading}>
              Send
            </button>
          </form>
        </section>
      )}
    </div>
  );
};

export default ForecAICopilot;
